import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import Grid from "@material-ui/core/Grid";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import Typography from "@material-ui/core/Typography";
import {Client} from "@microsoft/microsoft-graph-client";
import {google} from "googleapis";
import JSZip from "jszip";
import saveAs from "jszip/vendor/FileSaver";
import _ from "lodash";
import {UserAgentApplication} from "msal";
import PropTypes from "prop-types";
import React, {Fragment, memo, useRef, useState} from "react";
import UrlPattern from "url-pattern";
import * as XLSX from "xlsx";
import {
    IOS_RESOURCE_FILENAME,
    KEY,
    LANGUAGE_CODES,
    PLATFORM_ANDROID,
    PLATFORM_FLUTTER,
    PLATFORM_I18NEXT,
    PLATFORM_IOS,
    PLATFORM_PHP_LARAVEL
} from "../constant";
import useLocalStorageState from "../hooks/useLocalStorageState";
import SheetInformation from "../models/SheetInformation";
import AndroidResourceWriter from "../writers/resource/AndroidResourceWriter";
import FlutterResourceWriter from "../writers/resource/FlutterResourceWriter";
import IOSResourceWriter from "../writers/resource/IOSResourceWriter";
import BootstrapInput from "./common/BootstrapInput";
import CustomCheckbox from "./common/CustomCheckbox";
import PhpLaravelResourceWriter from "../writers/resource/PhpLaravelResourceWriter";
import I18NextResourceWriter from "../writers/resource/I18NextResourceWriter";

const userAgentApplication = new UserAgentApplication({
    auth: {
        clientId: "02f99566-c426-42b3-837a-5eae286a86de"
    },
    cache: {
        cacheLocation: "localStorage"
    }
});

const ExcelConverter = props => {
    const fileInput = useRef();
    const [excelFile, setExcelFile] = useState("");
    const [platform, setPlatform] = useLocalStorageState("ExcelConverter.state.platform", PLATFORM_ANDROID);
    const [trimWhitespaces, setTrimWhitespaces] = useLocalStorageState("ExcelConverter.state.trimWhitespaces", true);
    const [replacePlaceholders, setReplacePlaceholders] = useLocalStorageState("ExcelConverter.state.replacePlaceholders", true);
    const [replaceFullWidthPlaceholders, setReplaceFullWidthPlaceholders] = useLocalStorageState("ExcelConverter.state.replaceFullWidthPlaceholders", true);
    const [replaceWhitespaceCharacters, setReplaceWhitespaceCharacters] = useLocalStorageState("ExcelConverter.state.replaceWhitespaceCharacters", true);
    const [retainEmptyValue, setRetainEmptyValue] = useLocalStorageState("ExcelConverter.state.retainEmptyValue", false);
    const [evaluateFormula, setEvaluateFormula] = useLocalStorageState("ExcelConverter.state.evaluateFormula", true);
    const [mergeSheets, setMergeSheets] = useLocalStorageState("ExcelConverter.state.mergeSheets", false);
    let writers = [];

    const isFormulaEvaluationSupported = () => {
        try {
            const url = new URL(excelFile);
            if (url.host === "onedrive.live.com")
                return url.searchParams.get("resid");
        } catch (error) {
            console.log(error.message);
        }
        return fileInput.current && fileInput.current.value;
    };

    const handlePlatformChange = event => {
        setPlatform(event.target.value);
    };

    const handleCheckboxChange = event => {
        switch (event.target.name) {
            case "trimWhitespaces":
                return setTrimWhitespaces(event.target.checked);
            case "replacePlaceholders":
                return setReplacePlaceholders(event.target.checked);
            case "replaceFullWidthPlaceholders":
                return setReplaceFullWidthPlaceholders(event.target.checked);
            case "replaceWhitespaceCharacters":
                return setReplaceWhitespaceCharacters(event.target.checked);
            case "evaluateFormula":
                return setEvaluateFormula(event.target.checked);
            case "mergeSheets":
                return setMergeSheets(event.target.checked);
            case "retainEmptyValue":
                return setRetainEmptyValue(event.target.checked);
        }
    };

    const handleFilePathInputChange = event => {
        fileInput.current.value = "";
        setExcelFile(event.target.value);
    };

    const handleFileInputChange = event => {
        setExcelFile(_.get(event, "target.files[0].name", ""));
    };

    const handleConvertToResource = async () => {
        const {onLoad} = props;
        onLoad(true);
        const file = _.get(fileInput, "current.files[0]");
        if (file) {
            const reader = new FileReader();
            reader.onload = handleOnload;
            reader.onerror = handleOnerror;
            reader.readAsArrayBuffer(file);
        } else {
            try {
                const url = new URL(excelFile);
                if (url.host.endsWith("docs.google.com")) {
                    const result = new UrlPattern("/spreadsheets/d/(:id)/(*)").match(url.pathname);
                    if (result) {
                        getGoogleSheets(result.id);
                        return;
                    }
                } else if (url.host.endsWith("onedrive.live.com")) {
                    const id = url.searchParams.get("resid");
                    if (id) {
                        await getOnlineExcelSheets(id);
                        return;
                    }
                } else if (url.host.endsWith("1drv.ms") || url.host.endsWith("sharepoint.com")) {
                    alert("Shared links are not supported.");
                    onLoad(false);
                    return;
                }
                alert("URL not supported.");
                onLoad(false);
            } catch (error) {
                alert(error.message);
                onLoad(false);
            }
        }
    };

    const getWriter = (header, languageCode, sheetName) => {
        let writer = writers.find(writer => writer.header === header);
        if (writer) return writer;
        else {
            switch (platform) {
                case PLATFORM_ANDROID:
                    if (languageCode.android) {
                        writer = new AndroidResourceWriter(
                            languageCode.android,
                            trimWhitespaces,
                            replacePlaceholders,
                            replaceFullWidthPlaceholders,
                            replaceWhitespaceCharacters,
                            retainEmptyValue,
                            header);
                    }
                    break;
                case PLATFORM_IOS:
                    if (languageCode.ios) {
                        writer = new IOSResourceWriter(
                            languageCode.ios,
                            trimWhitespaces,
                            replacePlaceholders,
                            replaceFullWidthPlaceholders,
                            replaceWhitespaceCharacters,
                            retainEmptyValue,
                            header,
                            sheetName);
                    }
                    break;
                case PLATFORM_FLUTTER:
                    if (languageCode.flutter) {
                        writer = new FlutterResourceWriter(
                            languageCode.flutter,
                            trimWhitespaces,
                            replacePlaceholders,
                            replaceFullWidthPlaceholders,
                            replaceWhitespaceCharacters,
                            retainEmptyValue,
                            header);
                    }
                    break;
                case PLATFORM_I18NEXT:
                    if (languageCode.i18next) {
                        writer = new I18NextResourceWriter(
                            languageCode.i18next,
                            trimWhitespaces,
                            replacePlaceholders,
                            replaceFullWidthPlaceholders,
                            replaceWhitespaceCharacters,
                            retainEmptyValue,
                            header,
                            sheetName);
                    }
                    break;
                case PLATFORM_PHP_LARAVEL:
                    if (languageCode.phpLaravel) {
                        writer = new PhpLaravelResourceWriter(
                            languageCode.phpLaravel,
                            trimWhitespaces,
                            replacePlaceholders,
                            replaceFullWidthPlaceholders,
                            replaceWhitespaceCharacters,
                            retainEmptyValue,
                            header,
                            sheetName);
                    }
            }
        }
        if (writer) {
            writers.push(writer);
            return writer;
        }
    };

    const getGoogleSheets = sheetId => {
        const client = google.sheets({
            version: "v4",
            auth: "AIzaSyC_Ti5QRYyZZhW6y5uoyeWKNqpUKmN1UuU"
        });
        client.spreadsheets.get({
            spreadsheetId: sheetId,
            fields: "sheets.properties"
        }, async (error, response) => {
            const {onLoad} = props;
            const sheetProperties = _.get(response, "data.sheets");
            if (sheetProperties) {
                const defaultSheetName = IOS_RESOURCE_FILENAME.replace(".strings", "");
                let defaultSheetIndex = sheetProperties.findIndex(value => value.properties.title === defaultSheetName);
                if (defaultSheetIndex < 0) {
                    defaultSheetIndex = 0;
                }
                const zip = new JSZip();
                await writeGoogleSheet(client, sheetId, sheetProperties[defaultSheetIndex].properties.title);
                switch (platform) {
                    case PLATFORM_ANDROID:
                    case PLATFORM_FLUTTER:
                        if (mergeSheets)
                            for (let i = 0; i < sheetProperties.length; i++) {
                                if (i === defaultSheetIndex) {
                                    continue;
                                }
                                await writeGoogleSheet(client, sheetId, sheetProperties[i].properties.title);
                            }
                        for (const writer of writers) writer.addToZip(zip);
                        writers = [];
                        break;
                    case PLATFORM_IOS:
                        for (const writer of writers) writer.addToZip(zip);
                        writers = [];
                        for (let i = 0; i < sheetProperties.length; i++) {
                            if (i === defaultSheetIndex) {
                                continue;
                            }
                            await writeGoogleSheet(client, sheetId, sheetProperties[i].properties.title);
                            for (const writer of writers) writer.addToZip(zip);
                            writers = [];
                        }
                }
                for (const writer of writers) writer.addToZip(zip);
                writers = [];
                try {
                    if (_.isEmpty(zip.files)) {
                        alert("File is empty. If your file has values, please make sure your header mappings match.");
                        return onLoad(false);
                    }
                    const blob = await zip.generateAsync({type: "blob"});
                    saveAs(blob, `${excelFile.split(".")[0]}_${platform}.zip`);
                } catch (error) {
                    alert(error.message);
                }
            } else if (error) alert(error.message);
            onLoad(false);
        });
    };

    const writeGoogleSheet = (client, sheetId, sheetName) => {
        return new Promise((resolve, reject) => {
            client.spreadsheets.values.get({
                spreadsheetId: sheetId,
                range: sheetName
            }, (error, response) => {
                if (response) {
                    const {languageCodes} = props;
                    const headers = response.data.values[0];
                    let keyColumn = headers.findIndex(header => header === KEY);
                    if (keyColumn < 0) keyColumn = 0;
                    let keyMacro = getKeyMacro();
                    let valueMacro = getValueMacro();
                    for (let row = 1; row < response.data.values.length; row++) {
                        const key = response.data.values[row][keyColumn];
                        for (let column = 0; column < headers.length; column++) {
                            let header = headers[column];
                            const languageCode = (languageCodes || LANGUAGE_CODES).find(code => code.name === header);
                            if (column === keyColumn || !header || !key || !languageCode) continue;
                            header = header.trim();
                            let modifiedKey = key;
                            if (keyMacro) {
                                modifiedKey = keyMacro(modifiedKey);
                                if (typeof modifiedKey !== "string" || !modifiedKey) modifiedKey = key;
                            }
                            let modifiedValue = _.get(response.data, `values[${row}][${column}]`, "");
                            if (valueMacro) {
                                modifiedValue = valueMacro(modifiedValue);
                                if (typeof modifiedValue !== "string" || !modifiedValue) modifiedValue = _.get(response.data, `values[${row}][${column}]`, "");
                            }
                            let writer = getWriter(header, languageCode, sheetName);
                            if (writer) {
                                writer.write(modifiedKey, modifiedValue);
                            }
                        }
                    }
                    return resolve();
                }
                return reject(error);
            });
        });
    };

    const getOnlineExcelSheets = async (workbookId) => {
        const {onLoad} = props;
        onLoad(true);
        const graphScopes = ["files.read.all"];
        const client = Client.init({
            authProvider: async done => {
                try {
                    const tokenResponse = await userAgentApplication.acquireTokenSilent({scopes: graphScopes});
                    done(null, tokenResponse.accessToken);
                } catch (error) {
                    done(error);
                }
            }
        });
        try {
            if (!userAgentApplication.getAccount()) await userAgentApplication.loginPopup({scopes: graphScopes});
            const worksheetsResponse = await client.api(`/me/drive/items/${workbookId}/workbook/worksheets`).get();
            const defaultSheetName = IOS_RESOURCE_FILENAME.replace(".strings", "");
            let defaultSheetIndex = worksheetsResponse.value.findIndex(value => value.name === defaultSheetName);
            if (defaultSheetIndex < 0) {
                defaultSheetIndex = 0;
            }
            const zip = new JSZip();
            await writeOnlineExcelSheet(client, workbookId, worksheetsResponse.value[defaultSheetIndex].name);
            switch (platform) {
                case PLATFORM_ANDROID:
                case PLATFORM_FLUTTER:
                    if (mergeSheets)
                        for (let i = 0; i < worksheetsResponse.value.length; i++) {
                            if (i === defaultSheetIndex) {
                                continue;
                            }
                            await writeOnlineExcelSheet(client, workbookId, worksheetsResponse.value[i].name);
                        }
                    for (const writer of writers) writer.addToZip(zip);
                    writers = [];
                    break;
                case PLATFORM_IOS:
                    for (const writer of writers) writer.addToZip(zip);
                    writers = [];
                    for (let i = 0; i < worksheetsResponse.value.length; i++) {
                        if (i === defaultSheetIndex) {
                            continue;
                        }
                        await writeOnlineExcelSheet(client, workbookId, worksheetsResponse.value[i].name);
                        for (const writer of writers) writer.addToZip(zip);
                        writers = [];
                    }
            }
            for (const writer of writers) writer.addToZip(zip);
            writers = [];
            if (_.isEmpty(zip.files)) {
                alert("File is empty. If your file has values, please make sure your header mappings match.");
                return onLoad(false);
            }
            const blob = await zip.generateAsync({type: "blob"});
            saveAs(blob, `${excelFile.split(".")[0]}_${platform}.zip`);
        } catch (error) {
            alert(error.message);
        }
        onLoad(false);
    };

    const writeOnlineExcelSheet = async (client, workbookId, sheetName) => {
        const response = await client.api(`/me/drive/items/${workbookId}/workbook/worksheets/${sheetName}/usedRange`).get();
        const {languageCodes} = props;
        const propertyName = evaluateFormula ? "text" : "formulas";
        const headers = response[propertyName][0];
        let keyColumn = headers.findIndex(header => header === KEY);
        if (keyColumn < 0) keyColumn = 0;
        let keyMacro = getKeyMacro();
        let valueMacro = getValueMacro();
        for (let row = 1; row < response[propertyName].length; row++) {
            const key = response[propertyName][row][keyColumn];
            for (let column = 0; column < headers.length; column++) {
                let header = headers[column];
                const languageCode = (languageCodes || LANGUAGE_CODES).find(code => code.name === header);
                if (column === keyColumn || !header || !key || !languageCode) continue;
                header = header.trim();
                let modifiedKey = key;
                if (keyMacro) {
                    modifiedKey = keyMacro(modifiedKey);
                    if (typeof modifiedKey !== "string" || !modifiedKey) modifiedKey = key;
                }
                let modifiedValue = _.get(response, `${propertyName}[${row}][${column}]`, "");
                if (valueMacro) {
                    modifiedValue = valueMacro(modifiedValue);
                    if (typeof modifiedValue !== "string" || !modifiedValue) modifiedValue = _.get(response, `${propertyName}[${row}][${column}]`, "");
                }
                let writer = getWriter(header, languageCode, sheetName);
                if (writer) {
                    writer.write(modifiedKey, modifiedValue);
                }
            }
        }
    };

    const handleOnload = async event => {
        const {onLoad} = props;
        const workbook = XLSX.read(event.target.result, {type: "array"});
        const zip = new JSZip();
        if (platform === PLATFORM_I18NEXT || platform === PLATFORM_PHP_LARAVEL) {
            for (let sheetName of workbook.SheetNames) {
                const sheet = workbook.Sheets[sheetName];
                const information = new SheetInformation(sheet);
                writeExcelSheet(sheet, information, sheetName);
                for (const writer of writers) writer.addToZip(zip);
                writers = [];
            }
        } else {
            const defaultSheetName = IOS_RESOURCE_FILENAME.replace(".strings", "");
            let defaultSheetIndex = workbook.SheetNames.findIndex(value => value === defaultSheetName);
            if (defaultSheetIndex < 0) {
                defaultSheetIndex = 0;
            }
            let sheet = workbook.Sheets[workbook.SheetNames[defaultSheetIndex]];
            let information = new SheetInformation(sheet);
            writeExcelSheet(sheet, information);
            switch (platform) {
                case PLATFORM_ANDROID:
                case PLATFORM_FLUTTER:
                    if (mergeSheets)
                        for (let i = 0; i < workbook.SheetNames.length; i++) {
                            if (i === defaultSheetIndex) {
                                continue;
                            }
                            sheet = workbook.Sheets[workbook.SheetNames[i]];
                            information = new SheetInformation(sheet);
                            writeExcelSheet(sheet, information);
                        }
                    for (const writer of writers) writer.addToZip(zip);
                    writers = [];
                    break;
                case PLATFORM_IOS:
                    for (const writer of writers) writer.addToZip(zip);
                    writers = [];
                    for (let i = 0; i < workbook.SheetNames.length; i++) {
                        if (i === defaultSheetIndex) {
                            continue;
                        }
                        sheet = workbook.Sheets[workbook.SheetNames[i]];
                        information = new SheetInformation(sheet);
                        writeExcelSheet(sheet, information, workbook.SheetNames[i]);
                        for (const writer of writers) writer.addToZip(zip);
                        writers = [];
                    }
                    break;
            }
        }
        try {
            if (_.isEmpty(zip.files)) {
                alert("File is empty. If your file has values, please make sure your header mappings match.");
                return onLoad(false);
            }
            const blob = await zip.generateAsync({type: "blob"});
            saveAs(blob, `${excelFile.split(".")[0]}_${platform}.zip`);
        } catch (error) {
            alert(error.message);
        }
        onLoad(false);
    };

    const handleOnerror = async event => {
        alert(event.target.error.message);
        event.target.abort();
        props.onLoad(false);
    };

    const writeExcelSheet = (sheet, information, sheetName) => {
        const {languageCodes} = props;
        let keyMacro = getKeyMacro();
        let valueMacro = getValueMacro();
        for (let row = 0; row < information.keys.length; row++)
            for (let column = 0; column < information.headers.length; column++) {
                let header = information.headers[column];
                const languageCode = (languageCodes || LANGUAGE_CODES).find(code => code.name === header);
                if (column === information.keyColumn || !header || !information.keys[row] || !languageCode) continue;
                header = header.trim();
                const value = sheet[XLSX.utils.encode_cell({r: row + 1, c: column})];
                if (value || retainEmptyValue) {
                    let modifiedKey = information.keys[row];
                    if (keyMacro) {
                        modifiedKey = keyMacro(modifiedKey);
                        if (typeof modifiedKey !== "string" || !modifiedKey) modifiedKey = information.keys[row];
                    }
                    let modifiedValue = getCellValue(value);
                    if (valueMacro) {
                        modifiedValue = valueMacro(modifiedValue);
                        if (typeof modifiedValue !== "string" || !modifiedValue) modifiedValue = getCellValue(value);
                    }
                    let writer = getWriter(header, languageCode, sheetName);
                    if (writer) {
                        writer.write(modifiedKey, modifiedValue);
                    }
                }
            }
    };

    const getCellValue = value => {
        if (value) {
            if (value.t === "e") {
                return "";
            }
            if (evaluateFormula) {
                return `${value.v || ""}`;
            }
            return `${value.f || value.v || ""}`;
        } else {
            return "";
        }
    };

    const getKeyMacro = () => {
        try {
            const keyMacro = eval(props.keyMacro);
            if (typeof keyMacro === "function") return keyMacro;
        } catch (error) {
            console.log(error.message);
        }
        return null;
    };

    const getValueMacro = () => {
        try {
            const valueMacro = eval(props.valueMacro);
            if (typeof valueMacro === "function") return valueMacro;
        } catch (error) {
            console.log(error.message);
        }
        return null;
    };

    return (
        <Fragment>
            <Typography variant="h4" gutterBottom>Convert excel to app resources</Typography>
            <Grid container spacing={2} alignItems={"center"}>
                <Grid item xs={12} sm={8} md={8}>
                    <BootstrapInput value={excelFile}
                                    placeholder="Excel Online or Google Sheet url"
                                    onChange={handleFilePathInputChange}
                                    fullWidth/>
                    <Input inputRef={fileInput}
                           onChange={handleFileInputChange}
                           accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                           id="excelFile"
                           type="file"/>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                    <InputLabel htmlFor="excelFile">
                        <Button component="span"
                                variant="contained"
                                color="primary"
                                fullWidth>
                            Select file
                        </Button>
                    </InputLabel>
                </Grid>
            </Grid>
            <Grid container spacing={2} alignItems={"center"}>
                <Grid item xs={4} sm={3} md={3}>
                    <InputLabel>Platform:</InputLabel>
                </Grid>
                <Grid item xs={8} sm={5} md={5}>
                    <FormControl fullWidth>
                        <Select value={platform}
                                onChange={handlePlatformChange}
                                input={<BootstrapInput/>}>
                            <MenuItem value={PLATFORM_ANDROID}>{PLATFORM_ANDROID}</MenuItem>
                            <MenuItem value={PLATFORM_IOS}>{PLATFORM_IOS}</MenuItem>
                            <MenuItem value={PLATFORM_FLUTTER}>{PLATFORM_FLUTTER}</MenuItem>
                            <MenuItem value={PLATFORM_I18NEXT}>{PLATFORM_I18NEXT}</MenuItem>
                            <MenuItem value={PLATFORM_PHP_LARAVEL}>{PLATFORM_PHP_LARAVEL}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                    <Button onClick={handleConvertToResource}
                            variant="contained"
                            color="primary"
                            fullWidth>
                        Convert to resource
                    </Button>
                </Grid>
            </Grid>
            <Grid container spacing={2} alignItems={"center"}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Trim whitespaces"
                                    onChange={handleCheckboxChange}
                                    name="trimWhitespaces"
                                    checked={trimWhitespaces}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Replace formatting placeholders"
                                    onChange={handleCheckboxChange}
                                    name="replacePlaceholders"
                                    checked={replacePlaceholders}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Replace full width placeholders"
                                    onChange={handleCheckboxChange}
                                    name="replaceFullWidthPlaceholders"
                                    checked={replaceFullWidthPlaceholders}
                                    disabled={!replacePlaceholders}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Replace whitespace characters"
                                    onChange={handleCheckboxChange}
                                    name="replaceWhitespaceCharacters"
                                    checked={replaceWhitespaceCharacters}
                                    disabled={platform === PLATFORM_FLUTTER || platform === PLATFORM_I18NEXT}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Retain empty value"
                                    onChange={handleCheckboxChange}
                                    name="retainEmptyValue"
                                    checked={retainEmptyValue}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Evaluate formula"
                                    onChange={handleCheckboxChange}
                                    name="evaluateFormula"
                                    checked={evaluateFormula}
                                    disabled={!isFormulaEvaluationSupported()}/>
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                    <CustomCheckbox label="Merge sheets"
                                    onChange={handleCheckboxChange}
                                    name="mergeSheets"
                                    checked={mergeSheets}
                                    disabled={platform === PLATFORM_IOS}/>
                </Grid>
            </Grid>
        </Fragment>
    );
};

ExcelConverter.propTypes = {
    languageCodes: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        android: PropTypes.string,
        ios: PropTypes.string
    })),
    onLoad: PropTypes.func,
    keyMacro: PropTypes.string,
    valueMacro: PropTypes.string
};

export default memo(ExcelConverter);
